import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Empty, List, Space, Typography, message } from "antd";

import FileCreateModal from "./components/FileCreateModal";
import type { PanchangamFileOpsService } from "./services/panchangam-file-ops.service";
import { panchangamFileOpsService } from "./services/service-bootstrap";
import type { JsonValue, PanchangamFolderFile, PanchangamFolderState } from "./types";
import { getPanchangamAccessForEmail } from "./utils/access";

export interface ConfigEditorScreenProps {
  userEmail: string;
  fileOpsService?: PanchangamFileOpsService;
  onOpenFile?: (folderId: string, fileId: string) => void;
}

export const ConfigEditorScreen: React.FC<ConfigEditorScreenProps> = ({ userEmail, fileOpsService = panchangamFileOpsService, onOpenFile }) => {
  const access = useMemo(() => getPanchangamAccessForEmail(userEmail), [userEmail]);
  const canAccess = access.isAllowed;
  const canAdmin = access.isAdmin;

  const [folders, setFolders] = useState<PanchangamFolderState[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState(false);

  const getFileSummary = (file: PanchangamFolderFile): { latestVersion: number; deployedVersion: number | null } => {
    let latestVersion = 0;
    let deployedVersion: number | null = null;

    file.meta_data.history.forEach((entry) => {
      if (entry.created_version != null) {
        latestVersion = Math.max(latestVersion, entry.created_version);
      }
      deployedVersion = entry.deployed_version;
    });

    return { latestVersion, deployedVersion };
  };

  const fetchFolders = useCallback(async () => {
    if (!canAccess) {
      return;
    }

    try {
      const nextFolders = await fileOpsService.loadFolders();
      setFolders(nextFolders);

      if (!selectedFolderId && nextFolders.length > 0) {
        setSelectedFolderId(nextFolders[0].folder.id);
      }
    } catch (error) {
      message.error("Failed to load config folders");
    }
  }, [canAccess, fileOpsService, selectedFolderId]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const updateFolderState = (nextState: PanchangamFolderState): void => {
    setFolders((current) =>
      current.map((entry) =>
        entry.folder.id === nextState.folder.id ? nextState : entry,
      ),
    );
  };

  const activeFolder = folders.find((entry) => entry.folder.id === selectedFolderId) || null;

  const handleCreateFile = async ({ fileName, seedJson }: { fileName: string; seedJson: JsonValue }) => {
    if (!activeFolder) {
      throw new Error("Select a folder before creating a file");
    }

    if (!canAdmin) {
      throw new Error("Only admins can add files");
    }

    const blob = new Blob([JSON.stringify(seedJson, null, 2)], { type: "application/json" });
    const file = new File([blob], `${fileName}.json`, { type: "application/json" });

    const nextFolderState = await fileOpsService.addFile({
      folderId: activeFolder.folder.id,
      fileName,
      file,
      authorEmail: userEmail,
      userName: userEmail,
    });

    updateFolderState(nextFolderState);
    message.success("File created successfully");
  };

  if (!canAccess) {
    return (
      <div style={{ padding: "0 40px" }}>
        <Alert
          type="warning"
          showIcon
          message="Access denied"
          description="Your email is not allowlisted for Panchangam config management."
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "0 40px" }}>
      <Card
        title="Config Editor"
        extra={
          <Space>
            <Typography.Text type="secondary">{userEmail}</Typography.Text>
            {canAdmin ? (
              <Button type="primary" onClick={() => setShowCreateModal(true)} disabled={!activeFolder}>
                Add File
              </Button>
            ) : null}
          </Space>
        }
      >
        {folders.length === 0 ? (
          <div style={{ display: "flex", gap: 16, width: "100%" }}>
            <div style={{ flex: "0 0 25%" }}>
              <Card title="Folders">
                <Empty description="No folders configured yet" />
              </Card>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 16, width: "100%" }}>
            <div style={{ flex: "0 0 25%" }}>
              <Card title="Folders">
                <List
                  size="small"
                  bordered
                  dataSource={folders}
                  renderItem={(item) => (
                    <List.Item
                      style={{ cursor: "pointer", background: item.folder.id === selectedFolderId ? "#f0f5ff" : undefined }}
                      onClick={() => setSelectedFolderId(item.folder.id)}
                    >
                      <div>
                        <Typography.Text strong>{item.folder.id}</Typography.Text>
                        <div>
                          <Typography.Text type="secondary">{item.payload.files.length} files</Typography.Text>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </Card>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <Card title={`Files${activeFolder ? ` • ${activeFolder.folder.id}` : ""}`}>
                <List
                  size="small"
                  bordered
                  dataSource={activeFolder?.payload.files ?? []}
                  locale={{ emptyText: "No files in this folder" }}
                  renderItem={(item) => (
                    <List.Item
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        activeFolder
                          ? onOpenFile?.(activeFolder.folder.id, item.id)
                          : null
                      }
                    >
                      <div>
                        <Typography.Text strong>{item.id}</Typography.Text>
                        <div>
                          <Typography.Text type="secondary">
                            v{getFileSummary(item).latestVersion || "-"} • deployed {getFileSummary(item).deployedVersion ?? "none"}
                          </Typography.Text>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </Card>
            </div>


          </div>
        )}
      </Card>

      <FileCreateModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateFile}
      />
    </div>
  );
};

export default ConfigEditorScreen;
